/**
 * Dashboard switcher popover — saved-dashboard list, inline "New dashboard"
 * creation, per-row hover actions (Rename/Duplicate/Delete — reusing the
 * Object Explorer node's own action functions), a filter input once the list
 * gets long, and the free-tier meter row (prototype `.pro-row`). The meter is
 * display-only; enforcement lives in the main-process repository (renderer
 * `hasProAccess` is a UX nicety only — S16). The "New" control preemptively
 * disables at the free cap (with a WHY tooltip) since creating is a single
 * atomic action with no legitimate reason to stay reachable past the cap.
 */
import { useRef, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useHasProAccess, useProRequiredHandler } from '@/core/licensing';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { FREE_DASHBOARD_LIMIT, type Dashboard } from '../types/dashboardTypes';
import {
  renameDashboardFromExplorer,
  saveDashboardParametersFromExplorer,
  saveDashboardPlaylistFromExplorer,
  duplicateDashboardFromExplorer,
  deleteDashboardFromExplorer,
} from '../explorer/dashboardExplorerActions';
import { DashboardSettingsDialog } from './DashboardSettingsDialog';
import { DashboardSwitcherRow } from './DashboardSwitcherRow';
import { DashboardFreeMeter } from './DashboardFreeMeter';

type DashboardSwitcherProps = {
  dashboards: Dashboard[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
};

/** Shared with the explorer node's disabled-duplicate tooltip (dashboardExplorerContextMenu.ts). */
const CAP_REASON = `Free plan is limited to ${FREE_DASHBOARD_LIMIT} dashboards — upgrade to Pro for unlimited.`;

/** Filter input only earns its keep once scrolling a plain list gets tedious. */
const FILTER_THRESHOLD = 6;

export function DashboardSwitcher({
  dashboards,
  activeId,
  onSelect,
  onCreate,
}: DashboardSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [renameTarget, setRenameTarget] = useState<Dashboard | null>(null);
  const hasProAccess = useHasProAccess();
  const { showUpgradePrompt } = useProRequiredHandler();
  const { track } = useAnalytics();
  const filterTracked = useRef(false);
  const active = dashboards.find((d) => d.id === activeId);
  const count = dashboards.length;
  const atCap = count >= FREE_DASHBOARD_LIMIT;
  const blocked = atCap && !hasProAccess;
  const showFilter = count > FILTER_THRESHOLD;
  const filtered = showFilter
    ? dashboards.filter((d) => d.name.toLowerCase().includes(filterQuery.trim().toLowerCase()))
    : dashboards;

  const create = () => {
    if (blocked) return;
    const name = newName.trim();
    if (!name) return;
    onCreate(name);
    setNewName('');
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setFilterQuery('');
      filterTracked.current = false;
    }
  };

  const handleFilterChange = (value: string) => {
    setFilterQuery(value);
    if (value.trim() && !filterTracked.current) {
      filterTracked.current = true;
      track('feature:dashboard_switcher_filter');
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold text-foreground hover:bg-muted"
            title="Switch dashboard"
            data-testid="dashboard-switcher-button"
          >
            <span className="max-w-56 truncate" title={active?.name ?? 'Dashboards'}>{active?.name ?? 'Dashboards'}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2" data-testid="dashboard-switcher">
          {showFilter && (
            <Input
              value={filterQuery}
              onChange={(e) => handleFilterChange(e.target.value)}
              placeholder="Filter dashboards…"
              className="mb-1 h-7 text-sm"
              data-testid="dashboard-switcher-filter-input"
              autoFocus
            />
          )}
          <div className="flex flex-col gap-0.5" role="listbox" aria-label="Saved dashboards">
            {filtered.map((d) => (
              <DashboardSwitcherRow
                key={d.id}
                dashboard={d}
                isActive={d.id === activeId}
                duplicateBlocked={blocked}
                duplicateBlockedReason={CAP_REASON}
                onSelect={() => {
                  onSelect(d.id);
                  setOpen(false);
                }}
                onRename={() => {
                  setOpen(false);
                  setRenameTarget(d);
                }}
                onDuplicate={() => {
                  if (blocked) return;
                  setOpen(false);
                  void duplicateDashboardFromExplorer(d.id);
                }}
                onDelete={() => {
                  setOpen(false);
                  void deleteDashboardFromExplorer(d.id);
                }}
              />
            ))}
            {dashboards.length === 0 && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">No dashboards yet</div>
            )}
            {dashboards.length > 0 && filtered.length === 0 && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">No dashboards match "{filterQuery}"</div>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5 border-t border-border pt-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && create()}
              placeholder={blocked ? 'Free plan limit reached' : 'New dashboard name'}
              disabled={blocked}
              className="h-7 text-sm"
              data-testid="dashboard-switcher-new-input"
            />
            <span
              title={blocked ? CAP_REASON : newName.trim() ? 'Create dashboard' : 'Enter a name first'}
            >
              <Button
                size="sm"
                // A disabled solid `default` Button only gets the base
                // opacity-50 — white-on-gray still reads as an enabled filled
                // button (S24 F15). Swap to muted tokens so the at-cap /
                // no-name state is visually unmistakable next to the muted input.
                className="h-7 px-2 disabled:bg-muted disabled:text-muted-foreground"
                disabled={blocked || !newName.trim()}
                onClick={create}
                data-testid="dashboard-switcher-create-button"
              >
                <Plus className="h-3.5 w-3.5" />
                New
              </Button>
            </span>
          </div>
          {!hasProAccess && (
            <DashboardFreeMeter count={count} onUpgradeClick={() => showUpgradePrompt()} />
          )}
        </PopoverContent>
      </Popover>
      {/* Sibling of the Popover (not nested inside it) — mirrors the Object
          Explorer node's own "Settings…" wiring in DatabaseExplorer.tsx, which
          renders this same dialog outside any menu/popover primitive. */}
      {renameTarget && (
        <DashboardSettingsDialog
          dashboard={renameTarget}
          dashboards={dashboards}
          open={!!renameTarget}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setRenameTarget(null);
          }}
          onRename={(name) => {
            void renameDashboardFromExplorer(renameTarget.id, name);
          }}
          onSaveParameters={(parameters) => {
            void saveDashboardParametersFromExplorer(renameTarget.id, parameters);
          }}
          onSavePlaylist={(playlist) => {
            void saveDashboardPlaylistFromExplorer(renameTarget.id, playlist);
          }}
        />
      )}
    </>
  );
}
